import React from "react";
import styled from "styled-components";
import CodeDisplay from "../utils/CodeDisplay";
import GitHubButton from "../utils/GitHubButton";
import TextShift from "../utils/TextShift";
import RevealOnScroll from "../utils/RevealOnScroll";

// Styled Components
const PageWrapper = styled.div`
  width: 60%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
    width: 90%;
  }
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: white;
  font-family: "Maitree", sans-serif;
  margin-bottom: 1rem;
`;

const SectionText = styled.p`
  color: #b5b5b5;
  font-family: "Maitree", sans-serif;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 2rem;
`;

const ProjectTitle = styled.h1`
  color: white;
  font-family: "Maitree", sans-serif;
  margin: 0;
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 2rem 0;
  gap: 1rem;
`;

const dynamicConfigCode = `
import os
import config

SERVICE_KEY = os.getenv("SERVICE_KEY", config.SERVICE_KEY)
SPREAD_SHEET = os.getenv("SPREAD_SHEET", config.SPREAD_SHEET)
`;

const credentialsCode = `
def initialize_sheet():
    scope = [
        "https://www.googleapis.com/auth/spreadsheets",
        "https://www.googleapis.com/auth/drive",
    ]

    if os.path.isfile(SERVICE_KEY):
        creds = ServiceAccountCredentials.from_json_keyfile_name(SERVICE_KEY, scope)
    else:
        with tempfile.NamedTemporaryFile(mode="w", delete=False) as temp_file:
            temp_file.write(SERVICE_KEY)
            temp_file_path = temp_file.name
        creds = ServiceAccountCredentials.from_json_keyfile_name(temp_file_path, scope)
        os.unlink(temp_file_path)
    return gspread.authorize(creds).open(SPREAD_SHEET).sheet1
`;

const sheetsIntegrationCode = `
def add_or_update_manga(url, manga_details, to_email):
    sheet = initialize_sheet()
    try:
        cell = sheet.find(url)
        if cell:
            row = cell.row
            sheet.update(f"A{row}:D{row}", [[url, *manga_details]])
        else:
            sheet.append_row([url, *manga_details])
    except Exception as e:
        print(f"An error occurred: {e}")
`;

const notificationCode = `
def notify_new_chapter(chapter_title, chapter_url, to_email):
    subject = f"New Chapter Alert: {chapter_title}"
    message = f"A new chapter is available now!\\n\\nTitle: {chapter_title}\\nRead here: {chapter_url}"
    send_email(subject, message, to_email)
`;

const sendEmailCode = `
def send_email(subject, message, to_email):
    service = get_gmail_service()
    msg = MIMEMultipart()
    msg["to"] = to_email
    msg["from"] = "me"
    msg["subject"] = subject
    msg.attach(MIMEText(message, "plain"))

    raw = base64.urlsafe_b64encode(msg.as_bytes()).decode()
    body = {"raw": raw}

    try:
        message = service.users().messages().send(userId="me", body=body).execute()
        print("Email sent! Message Id: %s" % message["id"])
    except Exception as e:
        print("An error occurred: %s" % e)
`;

const getPageCode = `
def get_manga_page(url):
    """Fetch the webpage and return its content."""
    try:
        response = requests.get(url)
        response.raise_for_status()
        return response.text
    except requests.RequestException as e:
        print(f"Error fetching {url}: {e}")
        return None
`;

const scrapeDataCode = `
def scrape_manga_data(url):
    """Scrape the manga details from the webpage."""
    html_content = get_manga_page(url)
    if html_content:
        soup = BeautifulSoup(html_content, "html.parser")
        chapter_link = soup.find("a", class_="chapter-name text-nowrap")
        if chapter_link:
            chapter_url = chapter_link.get("href")
            chapter_full_title = chapter_link.get("title")
            if chapter_full_title:
                title_parts = chapter_full_title.split(" Chapter ")
                manga_title = title_parts[0] if title_parts else chapter_full_title
            else:
                manga_title = "Title not found"
            chapter_text = chapter_link.text.strip()
            return (chapter_url, manga_title, chapter_text)
        else:
            return (url, "Chapter link not found", "")
    else:
        return (url, "Failed to fetch", "")
`;

const encodeTokenCode = `
import base64

# Open the token.pickle file in binary mode
with open("token.pickle", "rb") as file:
    # Encode the file content into Base64
    encoded = base64.b64encode(file.read()).decode("utf-8")

# Write the Base64 string to a new file
with open("token.pickle.b64", "w") as out_file:
    out_file.write(encoded)

print("Base64 encoding complete! Saved as token.pickle.b64")
`;

const githubActionsCode = `
name: Run Macker Script

on:
  schedule:
    - cron: "0 10,22 * * *" # Runs daily at 10:00 AM and 10:00 PM UTC
  workflow_dispatch: # Allow manual triggering
    inputs: {}

jobs:
  run-macker:
    runs-on: ubuntu-latest

    steps:
    - name: Checkout Repository
      uses: actions/checkout@v3

    - name: Set Up Python
      uses: actions/setup-python@v4
      with:
        python-version: "3.x"

    - name: Install Dependencies
      run: pip install -r requirements.txt

    - name: Decode token.pickle
      env:
        BASE64_TOKEN: \${{ secrets.ENCODED_TOKEN }}
      run: echo \${BASE64_TOKEN} | base64 --decode > token.pickle

    - name: Run Script
      run: python macker.py
`;

const Macker = () => {
  return (
    <>
      <PageWrapper>
        <RevealOnScroll>
          <TitleWrapper>
            <TextShift 
              text="Macker" 
              fontFamily="moret-variable"
              fontSize="2rem"
            />
            <GitHubButton url="https://github.com/PinknMatter/Macker" />
          </TitleWrapper>
        </RevealOnScroll>
        <Section>
          <SectionTitle>Overview</SectionTitle>
          <SectionText>
            Macker is a Python script for managing manga updates. It dynamically
            loads configurations, handles Google API credentials securely, and
            provides the ability to add or update manga details efficiently. As
            well as send an email notification when a new chapter is available.
            The script is hosted autonomously on GitHub Actions and runs twice a
            day to fetch the latest manga chapters.
          </SectionText>
        </Section>

        {/* Dynamic Configuration Section */}
        <Section>
          <SectionTitle>Dynamic Configuration</SectionTitle>
          <SectionText>
            The script loads `SERVICE_KEY` and `SPREAD_SHEET` from environment
            variables or defaults in `config.py` to ensure flexibility.
          </SectionText>
          <CodeDisplay code={dynamicConfigCode} language="python" />
        </Section>

        {/* Secure Credential Handling Section */}
        <Section>
          <SectionTitle>Secure Credential Handling</SectionTitle>
          <SectionText>
            Macker supports both file-based and JSON content credentials for
            secure access to Google APIs.
          </SectionText>
          <CodeDisplay code={credentialsCode} language="python" />
        </Section>

        {/* Google Sheets Integration Section */}
        <Section>
          <SectionTitle>Google Sheets Integration</SectionTitle>
          <SectionText>
            The script interacts with Google Sheets to add or update manga
            details. It can locate existing entries or append new rows as
            needed.
          </SectionText>
          <CodeDisplay code={sheetsIntegrationCode} language="python" />
        </Section>
        <Section>
          <SectionTitle>Email Notifications</SectionTitle>
          <SectionText>
            Macker includes an integrated notification system to send emails
            about new chapter updates. The script leverages Google Gmail API for
            secure and automated email delivery.
          </SectionText>
          <CodeDisplay code={notificationCode} language="python" />

          <CodeDisplay code={sendEmailCode} language="python" />
        </Section>
        <Section>
          <SectionTitle>Web Scraping</SectionTitle>
          <SectionText>
            Macker includes a web scraping feature to automatically fetch the
            latest manga details from a given URL. It uses Python's `requests`
            library to fetch the webpage and `BeautifulSoup` to parse the HTML
            content.
          </SectionText>
          <CodeDisplay code={getPageCode} language="python" />
          <SectionText>
            The `scrape_manga_data` function extracts the manga title, chapter
            link, and chapter text from the webpage.
          </SectionText>
          <CodeDisplay code={scrapeDataCode} language="python" />
        </Section>
        <Section>
          <SectionTitle>Automation with GitHub Actions</SectionTitle>
          <SectionText>
            Macker is hosted on GitHub Actions and configured to run
            automatically twice a day at 10:00 AM and 10:00 PM UTC. This ensures
            the script fetches and updates the latest manga details without
            manual intervention.
          </SectionText>
          <CodeDisplay code={githubActionsCode} language="yaml" />

          <SectionTitle>Secure Token Handling</SectionTitle>
          <SectionText>
            To securely handle the `token.pickle` file required for Gmail API
            credentials, it is Base64 encoded and stored as a secret in GitHub
            Actions. The script decodes the token during runtime, ensuring no
            sensitive data is exposed in the repository.
          </SectionText>
          <CodeDisplay code={encodeTokenCode} language="python" />
        </Section>
      </PageWrapper>
    </>
  );
};

export default Macker;
